import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import { BASE_URL } from 'src/common/config'
import { clearQueryAfterAuth, getNewRouteByLang, getResultQuery, notifyIfChunkError } from 'src/common/functions'
import { postMessageFromWebviewToApp } from 'src/common/webview'
import { webviewRoutesArray } from 'src/router/webview-routes'
import { authBySlugs } from 'src/services/auth-by-slugs'

let alreadyBeforeEach = false

export default async function (ctx) {
  const { store } = ctx

  const routesList = await routes(store)

  const router = createRouter({
    scrollBehavior: (to, from, savedPosition) => {
      const routeNames = ['Home', 'Casino', 'GamesBySubGroup', 'GamesByGroup', 'BonusGames', 'GamesByManufacturer']

      if (routeNames.includes(to.name) && routeNames.includes(from.name)) {
        return savedPosition
      }
      return { left: 0, top: 0 }
    },
    routes: routesList,
    base: BASE_URL,
    history: createWebHistory(),
    linkExactActiveClass: 'exact-active',
  })

  router.beforeEach(async (to, from, next) => {
    const underAttack = store.getters['getFullCheck']
    const session = localStorage.getItem('ussidcs-tmp')

    if (underAttack && session && to.name !== 'session-auth') {
      return next({ name: 'session-auth', query: { url: to.fullPath } })
    }

    if (webviewRoutesArray.map((item) => item.name).includes(to.name)) {
      return next()
    }
    if (!alreadyBeforeEach && to.name !== 'auth-social') {
      alreadyBeforeEach = true

      const resolvedRoute = getNewRouteByLang(to, router, store)
      if (resolvedRoute) {
        return next(resolvedRoute)
      }
    }

    const nextRoute = await authBySlugs(to, store, router)
    if (nextRoute) {
      return next(nextRoute)
    }

    if (to.name === 'Auth') {
      const query = clearQueryAfterAuth(to.query, [])
      return next({ name: 'Home', query })
    }

    const newPath = to.query.path || to.query.url
    if (newPath && to.name !== 'Auth' && to.name !== 'session-auth') {
      const newRoute = router.resolve(decodeURIComponent(newPath))
      let paramsObject = {}
      try {
        const parseUrl = new URL(`${location.origin}${decodeURIComponent(newPath)}`)
        paramsObject = Object.fromEntries(Array.from(parseUrl.searchParams))
      } catch (e) {
        console.error(e)
      }

      const params = { ...(newRoute?.params ?? {}), locale: nextRoute?.locale || to.params.locale }
      const query = clearQueryAfterAuth({ ...to.query, ...paramsObject, ...(newRoute?.query ?? {}) })

      if (newRoute.name !== '404page') {
        return next({ name: newRoute.name, params, query })
      }

      const redirectRoute = temporaryRedirectForClients(newRoute, store)
      if (redirectRoute) {
        return next(redirectRoute)
      }
    }

    const liveChat = document.querySelector('#chat-widget-container')

    if (liveChat) {
      to.meta.isLiveChatHidden ? liveChat.classList.add('hidden') : liveChat.classList.remove('hidden')
    }

    const userAuth = store.getters.getAuth

    if (userAuth) {
      const prevUrlBeforePayment = localStorage.getItem('prev_url_cash_box')
      if (prevUrlBeforePayment) {
        localStorage.removeItem('prev_url_cash_box')
        try {
          const resolvedRoute = router.resolve(prevUrlBeforePayment)

          if (resolvedRoute.name !== '404page') {
            postMessageFromWebviewToApp({ type: 'payment_success', data: { type: 'payment_done' } })
            return next(resolvedRoute)
          }
        } catch (e) {
          console.error('Resolve route error', e)
        }
      }
    }

    const requireAuth = to.matched.some((record) => record.meta.auth)

    const needToSavePrevRoute = from.name && from.name !== 'GamePageNew'

    if (requireAuth) {
      if (userAuth) {
        next()
        if (needToSavePrevRoute) {
          store.dispatch('updatePrevRoute', from)
        }
        return
      } else {
        const userHasAccount = to.name === 'bonuses' && localStorage.getItem('hasAccount')

        const tab = userHasAccount ? 'login' : 'reg'
        const locationFullPath = router.resolve(to)?.fullPath

        if (locationFullPath) {
          sessionStorage.setItem('linkAfterAuth', locationFullPath)
        }

        next({ name: 'Home', params: to.params, query: { ...to.query, modal: tab } })
      }
    } else {
      const redirectRoute = temporaryRedirectForClients(to, store)
      if (redirectRoute) {
        return next(redirectRoute)
      }

      next()
    }

    if (needToSavePrevRoute) {
      store.dispatch('updatePrevRoute', { name: from.name, params: from.params, query: from.query, meta: from.meta })
    }
  })

  router.onError((e) => {
    notifyIfChunkError(e, store)
  })

  return router
}

function temporaryRedirectForClients(to, store) {
  const is404Page = to.name === '404page'

  if (store.getters.getIsAlt && !store.getters.getIsBot && is404Page) {
    const catchAll = (to.params?.catchAll ?? []).filter((i) => i !== '')

    const tailOfLink = Object.keys(to.query).length ? `?${getResultQuery(to.query)}` : ''
    if (!catchAll.length) {
      return null
    }

    const lang = to.params.locale || null
    const first = catchAll[0]

    const getRoute = (path) => (lang ? `/${lang}/${path}${tailOfLink}` : `/${path}${tailOfLink}`)

    if (catchAll.length === 1) {
      const match = {
        'drops-wins': 'drops',
        guarantee: 'rule/guarantee',
        security: 'rule/security',
        antifraud: 'rule/antifraud',
      }

      if (match[first]) {
        return getRoute(match[first])
      }

      return getRoute(`group/${first}`)
    }

    if (catchAll.length === 2) {
      const second = catchAll[1]
      return getRoute(`group/${first}/${second}`)
    }
  }
  return false
}
